import { modalTypes } from "constants/types";
import { hideModal, showModal } from "slices/app/actions/modals";
import { systemAdminRemoveUserFromWorkspace } from "slices/systemAdmin/actions/systemAdmin";
import {
  deleteUser,
  exportResultsImportUsersFromCsv,
} from "slices/systemAdmin/actions/users";
import {
  exportResultFromImportUsers,
  userAdminRemoveUserFromWorkspace,
} from "slices/userAdmin/actions/userAdmin";
import {
  deleteDictionary,
  setDefaultDictionary,
} from "slices/dictionary/actions/dictionary";
import { editTerm, validateTerms } from "slices/dictionary/dictionarySlice";
import { editItem } from "slices/settings/slices/acronymIgnoreSlice";
import { entryTypes, dictionaryTermTypes } from "constants/dictionary";
import React from "react";
import { setSearch, setSearchTerm } from "slices/discovery/discoverySlice";
import { getFilterDescription } from "constants/discovery";

export const searchWarningConfirmationModal = (message, dispatch) => ({
  headerText: "Warning",
  bodyText: message,
  okButton: {
    text: "Confirm",
    onOk: () => {
      dispatch(hideModal(modalTypes.CONFIRMATION));
    },
  },
});

export const systemAdminRemoveMemberModal = (
  userName,
  workspaceName,
  workspaceId,
  dispatch
) => ({
  headerText: "Remove Member",
  bodyText: `Are you sure you want to remove ${userName} from ${workspaceName}?`,
  okButton: {
    text: "Confirm",
    onOk: () => {
      dispatch(
        window.location.href.includes("systemAdmin")
          ? systemAdminRemoveUserFromWorkspace(
              userName,
              workspaceId,
              workspaceName
            )
          : userAdminRemoveUserFromWorkspace(
              userName,
              workspaceId,
              workspaceName
            )
      );
    },
  },
  cancelButton: {
    text: "Cancel",
    onCancel: () => {
      dispatch(hideModal(modalTypes.CONFIRMATION));
    },
  },
});

export const systemAdminDeleteUserModal = (username, dispatch) => ({
  headerText: "Delete User",
  bodyText: `Are you sure you want to delete ${username} from the system? Doing so will remove them from all workspaces they are members in and completely remove them from the system? Do you wish to proceed?`,
  okButton: {
    text: "Confirm",
    onOk: () => {
      dispatch(deleteUser(username));
    },
    cancelButton: {
      text: "Cancel",
      onCancel: () => {
        dispatch(hideModal(modalTypes.CONFIRMATION));
      },
    },
  },
});

export const systemAdminImportUsersFromCsvConfirmationModal = (
  userResults,
  resultsMessage,
  dispatch
) => ({
  headerText: "Users Imported",
  bodyText: `${resultsMessage}. Click Export Results to get full details.`,
  okButton: {
    text: "Export Results",
    onOk: () => {
      dispatch(exportResultsImportUsersFromCsv(userResults));
    },
  },
  cancelButton: {
    text: "Cancel",
    onCancel: () => {
      dispatch(hideModal(modalTypes.CONFIRMATION));
    },
  },
});

export const userAdminImportUsersFromCsvConfirmationModal = (
  userResults,
  resultsMessage,
  dispatch
) => ({
  headerText: "Users Imported",
  bodyText: `${resultsMessage}. Click Export Results to get full details.`,
  okButton: {
    text: "Export Results",
    onOk: () => {
      dispatch(exportResultFromImportUsers(userResults));
    },
  },
  cancelButton: {
    text: "Cancel",
    onCancel: () => {
      dispatch(hideModal(modalTypes.CONFIRMATION));
    },
  },
});

export const dictionaryTurnOffWildcard = (term, dispatch) => ({
  headerText: "Removing Wildcard?",
  bodyText:
    "You're making this a non-wildcard term. You will not be able to undo this, are you happy to continue?",
  okButton: {
    text: "Yes",
    onOk: () => {
      dispatch(
        editTerm({
          termId: term.id,
          categoryId: term.categoryId,
          editedTerm: {
            ...term,
            dictionaryTermType: dictionaryTermTypes.STRING,
          },
        })
      );
      dispatch(validateTerms());
      dispatch(hideModal(modalTypes.CONFIRMATION));
    },
  },
  cancelButton: {
    text: "Cancel",
    onCancel: () => {
      dispatch(hideModal(modalTypes.CONFIRMATION));
    },
  },
});

export const licenseSetupConfirmationModal = (dispatch, onClose) => ({
  headerText: "Confirmation",
  bodyText: "We have created the instance. Close to login",
  okButton: {
    text: "OK",
    onOk: () => {
      dispatch(hideModal(modalTypes.CONFIRMATION));
      onClose();
    },
  },
  onClose,
});

export const dictionaryDeleteDictionaryModal = ({
  dictionaryName,
  dictionaryId,
  folders,
  documents,
  navigate,
  dispatch,
}) => ({
  headerText: dictionaryName
    ? `Delete "${dictionaryName}"`
    : "Delete Dictionary",
  bodyText: (() => {
    const amountOfFolders = folders.length;
    const amountOfDocuments = documents.length;
    if (amountOfFolders || amountOfDocuments) {
      let message = "";
      message += `The "${dictionaryName}" dictionary is currently being used by`;
      if (amountOfFolders === 1) {
        message += ` ${amountOfFolders} folder`;
      }
      if (amountOfFolders > 1) {
        message += ` ${amountOfFolders} folders`;
      }
      if (amountOfFolders && amountOfDocuments) {
        message += " and";
      }
      if (amountOfDocuments === 1) {
        message += ` ${amountOfDocuments} document`;
      }
      if (amountOfDocuments > 1) {
        message += ` ${amountOfDocuments} documents`;
      }
      message += ". If you delete this dictionary, the";
      if (amountOfFolders) {
        message += " folders";
      }
      if (amountOfFolders && amountOfDocuments) {
        message += " and";
      }
      if (amountOfDocuments) {
        message += " documents";
      }
      message += " currently using it will be reset to the default dictionary.";
      message += "\n\nThis action cannot be undone. Do you wish to proceed?";
      return message;
    }
    return `"${dictionaryName}" contains some terms and/or categories. This action cannot be undone. Do you wish to proceed?`;
  })(),
  okButton: {
    text: "Yes",
    onOk: () => {
      dispatch(hideModal(modalTypes.CONFIRMATION));
      dispatch(deleteDictionary(dictionaryId, navigate));
    },
  },
  cancelButton: {
    text: "No",
    onCancel: () => {
      dispatch(hideModal(modalTypes.CONFIRMATION));
    },
  },
});

export const dictionarySetAsDefaultModal = (
  dictionaryName,
  dictionaryId,
  dispatch
) => ({
  headerText: "Set as Default Dictionary",
  bodyText: `Are you sure you wish to set ${dictionaryName} as the default dictionary?`,
  okButton: {
    text: "Ok",
    onOk: () => {
      dispatch(hideModal(modalTypes.CONFIRMATION));
      dispatch(setDefaultDictionary(dictionaryId, dictionaryName));
    },
  },
  cancelButton: {
    text: "Cancel",
    onCancel: () => {
      dispatch(hideModal(modalTypes.CONFIRMATION));
    },
  },
});

export const dictionaryDeleteTermModal = (entry, onOk, dispatch) => ({
  headerText: `Delete ${
    entry.type === entryTypes.CATEGORY ? "Category" : "Term"
  }`,
  bodyText:
    entry.type === entryTypes.CATEGORY ? (
      <span>
        Are you sure you want to delete category:{" "}
        <span style={{ fontWeight: "bold" }}>{entry.name}</span>, doing so will
        delete it and its associated terms. Do you wish to continue?
      </span>
    ) : (
      <span>
        Are you sure you want to delete term:{" "}
        <span style={{ fontWeight: "bold" }}>{entry.name}</span>. Do you wish to
        continue?
      </span>
    ),
  okButton: {
    text: "Confirm",
    onOk: () => {
      onOk();
      dispatch(hideModal(modalTypes.CONFIRMATION));
    },
  },
  cancelButton: {
    text: "Cancel",
    onCancel: () => {
      dispatch(hideModal(modalTypes.CONFIRMATION));
    },
  },
});

export const acronymIgnoreListTurnOffRegex = (index, editedItem, dispatch) => ({
  headerText: "Removing Regular Expression?",
  bodyText:
    "You're making this a non-regex term. You will not be able to undo this, are you happy to continue?",
  okButton: {
    text: "Yes",
    onOk: () => {
      dispatch(editItem({ index, editedItem }));
      dispatch(hideModal(modalTypes.CONFIRMATION));
    },
  },
  cancelButton: {
    text: "Cancel",
    onCancel: () => {
      dispatch(hideModal(modalTypes.CONFIRMATION));
    },
  },
});

export const changePasswordConfirmationModal = (dispatch) => ({
  headerText: "Change Password",
  bodyText: "Password updated successfully",
  okButton: {
    text: "OK",
    onOk: () => {
      dispatch(hideModal(modalTypes.CONFIRMATION));
    },
  },
});

export const displayNoFilterResults = (filterType) => (dispatch) => {
  const noFilterResultsConfirmationModal = () => ({
    headerText: "No Filter Results",
    bodyText: `We found no results for "${getFilterDescription(filterType)}" filter. Try a different filter`,
    okButton: {
      text: "Confirm",
      onOk: () => {
        dispatch(hideModal(modalTypes.CONFIRMATION));
      },
    },
    onClose: () => {
      dispatch(hideModal(modalTypes.CONFIRMATION));
    },
  });

  dispatch(
    showModal(modalTypes.CONFIRMATION, noFilterResultsConfirmationModal())
  );
};

export const displayNoDocumentsSelected = () => (dispatch) => {
  const noDocumentsSelectedConfirmationModal = () => ({
    headerText: "No Results",
    bodyText: `Cannot uncheck document, you must have at least one document checked.`,
    okButton: {
      text: "Confirm",
      onOk: () => {
        dispatch(hideModal(modalTypes.CONFIRMATION));
      },
    },
    onClose: () => {
      dispatch(hideModal(modalTypes.CONFIRMATION));
    },
  });

  dispatch(
    showModal(modalTypes.CONFIRMATION, noDocumentsSelectedConfirmationModal())
  );
};

export const displayCannotUncheckReferenceDoc = () => (dispatch) => {
  const cannotUncheckReferenceDocModal = () => ({
    headerText: "No Results",
    bodyText: `You cannot uncheck the reference document. Clear the filter and try again.`,
    okButton: {
      text: "Confirm",
      onOk: () => {
        dispatch(hideModal(modalTypes.CONFIRMATION));
      },
    },
    onClose: () => {
      dispatch(hideModal(modalTypes.CONFIRMATION));
    },
  });

  dispatch(
    showModal(modalTypes.CONFIRMATION, cannotUncheckReferenceDocModal())
  );
};

export const displayNoSearchResults = (searchTerm) => (dispatch, getState) => {
  const updateSearchTerm = () => {
    // Apply last successful search term
    const prevSearchTerm = getState().discovery.config.searchTerm;
    dispatch(setSearchTerm(null));
    queueMicrotask(() => {
      dispatch(setSearchTerm(prevSearchTerm));
    });
  };

  const noFilterResultsConfirmationModal = () => ({
    headerText: "No Search Results",
    bodyText: `We found no results for "${searchTerm}". Try searching for a different term`,
    okButton: {
      text: "Confirm",
      onOk: () => {
        updateSearchTerm();
        dispatch(hideModal(modalTypes.CONFIRMATION));
      },
    },
    onClose: () => {
      updateSearchTerm();
      dispatch(hideModal(modalTypes.CONFIRMATION));
    },
  });

  dispatch(
    showModal(modalTypes.CONFIRMATION, noFilterResultsConfirmationModal())
  );
};
